import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { api, errMsg, formatIDR, formatDuration } from "@/lib/api";
import { Avatar, Disclaimer } from "@/components/shared";
import { SlotPicker } from "@/components/SlotPicker";

export default function Booking() {
  const { talentId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [talent, setTalent] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ nickname: "", whatsapp: "", service_id: params.get("service") || "", duration_minutes: Number(params.get("duration")) || 0, age_confirmed: false, notes: "" });
  const [loading, setLoading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [slotsVersion, setSlotsVersion] = useState(0);
  const [initialSlot, setInitialSlot] = useState(params.get("slot") || null);

  useEffect(() => {
    api.get(`/talents/${talentId}`).then((r) => setTalent(r.data));
    api.get("/services").then((r) => setServices(r.data));
  }, [talentId]);

  const service = services.find((s) => s.id === form.service_id);
  const pkg = service?.packages.find((p) => p.minutes === form.duration_minutes);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.age_confirmed) return toast.error("Konfirmasi usia 18+ wajib dicentang");
    if (!pkg) return toast.error("Pilih layanan dan durasi");
    if (!scheduledAt) return toast.error("Pilih jadwal sesi dulu");
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, talent_id: talentId, scheduled_at: scheduledAt });
      toast.success(`Order ${data.code} dibuat. Selesaikan pembayaran dalam 15 menit.`);
      navigate(`/pay/${data.code}`);
    } catch (err) {
      toast.error(errMsg(err));
      if (err.response?.status === 409 || err.response?.status === 400) { setInitialSlot(null); setSlotsVersion((v) => v + 1); }
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <Link to={`/talent/${talentId}`} className="btn-ghost -ml-4 mb-6" data-testid="back-to-talent"><ArrowLeft size={16} strokeWidth={1.5} />Kembali</Link>
      <p className="eyebrow mb-3">Booking</p>
      <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-800">Hampir selesai</h1>
      <p className="mt-3 text-slate-600 leading-relaxed">Tanpa daftar akun. Kamu akan menerima kode order untuk memantau status.</p>

      {talent && (
        <div className="mt-8 card-soft p-4 flex items-center gap-4" data-testid="booking-talent-summary">
          <Avatar src={talent.avatar_url} name={talent.name} size={48} online={talent.is_online} />
          <div><p className="font-heading font-medium text-slate-800">{talent.name}</p><p className="text-sm text-slate-500">{service?.name} {pkg && `· ${formatDuration(pkg.minutes)} · ${formatIDR(pkg.price)}`}</p></div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="booking-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Layanan</span>
            <select className="input-soft mt-1.5" value={form.service_id} onChange={(e) => { set("service_id", e.target.value); set("duration_minutes", 0); setInitialSlot(null); }} data-testid="booking-service-select" required>
              <option value="">Pilih layanan</option>
              {services.filter((s) => talent?.service_ids.includes(s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Durasi</span>
            <select className="input-soft mt-1.5" value={form.duration_minutes} onChange={(e) => { set("duration_minutes", Number(e.target.value)); setInitialSlot(null); }} data-testid="booking-duration-select" required>
              <option value={0}>Pilih durasi</option>
              {service?.packages.map((p) => <option key={p.minutes} value={p.minutes}>{formatDuration(p.minutes)} — {formatIDR(p.price)}</option>)}
            </select>
          </label>
        </div>
        {pkg && (
          <div>
            <span className="text-sm font-medium text-slate-700">Jadwal sesi <span className="text-slate-400 font-normal">(WIB)</span></span>
            <SlotPicker talentId={talentId} duration={form.duration_minutes} value={scheduledAt} onChange={setScheduledAt} initial={initialSlot} refreshKey={slotsVersion} />
          </div>
        )}
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Nama panggilan</span>
          <input className="input-soft mt-1.5" placeholder="Boleh nama samaran" value={form.nickname} onChange={(e) => set("nickname", e.target.value)} data-testid="booking-nickname-input" required minLength={2} maxLength={40} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Nomor WhatsApp</span>
          <input className="input-soft mt-1.5" placeholder="08xxxxxxxxxx" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="booking-whatsapp-input" required inputMode="tel" />
          <span className="mt-1.5 text-xs text-slate-500 inline-flex items-center gap-1"><Lock size={12} strokeWidth={1.5} />Hanya terlihat oleh talent yang ditugaskan selama order aktif, lalu disamarkan.</span>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Catatan untuk talent (opsional)</span>
          <textarea className="input-soft mt-1.5 h-24 py-3" placeholder="Misal: lagi capek soal kerjaan, pengen didengerin aja." value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes-input" maxLength={300} />
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1 w-5 h-5 accent-[#6B8E7B]" checked={form.age_confirmed} onChange={(e) => set("age_confirmed", e.target.checked)} data-testid="booking-age-checkbox" />
          <span className="text-sm text-slate-700 leading-relaxed">Saya berusia 18 tahun atau lebih dan menyetujui <Link to="/syarat" className="underline text-primary">Syarat & Ketentuan</Link>, termasuk larangan konten seksual dan pelecehan.</span>
        </label>
        <Disclaimer />
        <button className="btn-primary w-full sm:w-auto" disabled={loading} data-testid="booking-submit-button">
          {loading ? "Memproses..." : pkg ? `Lanjut bayar ${formatIDR(pkg.price)}` : "Lanjut bayar"}
        </button>
      </form>
    </div>
  );
}
